import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { StyleSheet, View } from "react-native";
import { router } from "expo-router";
import { approxLabel, localTaxWords, merchDeliveryLabel, storeMoneyMinor as money } from "@libs/platform-wire";
import { deliveryDutiesNote } from "@libs/buy-on-behalf-client/delivery";
import { goodsWords } from "@libs/buy-on-behalf-client/goods";
import { orderLinesWords } from "@libs/buy-on-behalf-client/purchase-headline";
import type { Purchase } from "@libs/platform-wire/purchase";
import { PurchaseCheckout, type PurchaseDisplay } from "@libs/buy-on-behalf-checkout-web/purchase";
import { TICKETS_SHEET_CHECKOUT_APPEARANCE } from "@libs/shared-ui";
import LPressable from "@components/LPressable";
import { metrics } from "@utils/sentry";
import { Chevron } from "../native/Chevron";
import { OrderSummaryView } from "../native/OrderSummaryCard";
import { PayStatusButton } from "../native/PayStatusButton";
import { LOADING_BUTTON } from "../native/sheetButton";
import { VendorShape } from "./VendorShape";
import {
  purchaseOrderOf,
  vendorCheckoutKey,
  type OrderWords,
  type VendorCheckoutProps,
} from "./vendorCheckoutShared";

export type { VendorCheckoutProps };

/**
 * app-web's purchase checkout in the tickets sheet: the shared checkout's
 * purchase renderer (`PurchaseCheckout`, as Link in Bio mounts it) in the
 * app's light sheet, under the sheet's back and the order. The native
 * sibling (VendorCheckout.tsx) takes the same props; Metro picks one.
 *
 * The shared renderer draws its own primary button, status, fold and
 * attempt, the buyer's answers and VGS's card fields: the sheet draws no
 * button of its own and keeps no height. The buyer is the app's signed-in
 * fan, whose address the checkout cannot change (no ×). The order is the
 * one the checkout says it is for: a resumed attempt's own.
 */
export function VendorCheckout(props: VendorCheckoutProps) {
  // Another buyer or access: everything held here starts again.
  return <WebPurchaseCheckout key={vendorCheckoutKey(props.transport, props.accessId)} {...props} />;
}

/** What the shared checkout says it is for, as the summary words it: one
 *  delivery option is the country's ("UK delivery"), several the store's
 *  own name; a download its store emails says it is one. */
const orderOfDisplay = (
  purchase: Pick<Purchase, "title" | "delivery">,
  display: PurchaseDisplay,
  country: string | null,
): OrderWords => {
  const total = display.totalMinor;
  return {
    title: purchase.title,
    detail: orderLinesWords(display.lines, purchase),
    fee: display.delivery
      ? `${display.delivery.named ? display.delivery.option.name : merchDeliveryLabel(country)} ${
          display.delivery.option.feeMinor > 0 ? money(display.delivery.option.feeMinor, display.currency) : "free"
        }`
      : null,
    total: total === null ? "" : money(total, display.currency),
    approx: total !== null && total > 0 ? approxLabel(total, display.currency, display.approx) : null,
    tax: display.taxAdded ? localTaxWords(display.taxMinor, display.currency) : null,
    // The delivery chosen leaves import duties to the fan.
    duties: deliveryDutiesNote(display.delivery?.option),
  };
};

function WebPurchaseCheckout({
  transport,
  accessId,
  purchase,
  lines,
  delivery,
  deliveryCode,
  sheetId,
  deliverTo,
  restoreId,
  onAttempt,
  newId,
  onBack,
  onReselect,
  onChrome,
  onHolding,
  onSucceeded,
  clock,
  buyerCountry,
  onBlocked,
  initialDraft,
  onDraftChange,
  onPurchase,
}: VendorCheckoutProps) {
  useEffect(() => {
    onChrome?.({ button: null, hold: false });
  }, [onChrome]);

  // Held: the sheet may not be closed, nor the tickets gone back to.
  const [holding, setHolding] = useState(false);
  const holdingNow = useRef(onHolding);
  useLayoutEffect(() => {
    holdingNow.current = onHolding;
  });
  const hold = useCallback((next: boolean) => {
    setHolding(next);
    holdingNow.current?.(next);
  }, []);

  // What the checkout says it is for; the sheet's own order until it does.
  const [shown, setShown] = useState<PurchaseDisplay | null>(null);
  const country = purchase.delivery.kind === "ladder" || purchase.delivery.kind === "quoted" ? purchase.delivery.country : null;
  const order = shown && shown.totalMinor !== null
    ? orderOfDisplay(purchase, shown, deliverTo ?? country)
    : purchaseOrderOf(purchase, lines, null, null);
  // An event's tickets, or a merch product, as Link in Bio names them.
  const goodsText = goodsWords(purchase.goods, purchase.delivery.kind === "emailed");

  return (
    <View style={styles.screen} testID="vendor-web-checkout">
      <View style={styles.header}>
        {holding ? null : (
          <LPressable
            accessibilityLabel={goodsText.back}
            accessibilityRole="button"
            hitSlop={10}
            onPress={onBack}
            sentryEventName="VendorCheckout.Back"
            style={styles.back}
          >
            <Chevron />
          </LPressable>
        )}
      </View>
      <OrderSummaryView {...order} />
      <PurchaseCheckout
        transport={transport}
        accessId={accessId}
        lines={lines}
        delivery={delivery ?? null}
        deliveryCode={deliveryCode ?? null}
        sheetId={sheetId}
        deliverTo={deliverTo ?? null}
        appearance={TICKETS_SHEET_CHECKOUT_APPEARANCE}
        // Until the purchase is read: the checkout's shape, and the button
        // saying what it has since Continue.
        placeholder={
          <View style={styles.placeholder}>
            <VendorShape destination={goodsText.destination} />
            <PayStatusButton {...LOADING_BUTTON} />
          </View>
        }
        restoreId={restoreId}
        onAttempt={onAttempt}
        newId={newId}
        onHolding={hold}
        onSucceeded={onSucceeded}
        onReselect={onReselect}
        // The buyer is in the app already: its wallet, not the app link.
        onOpenWallet={() => openTickets(accessId)}
        onDisplay={setShown}
        clock={clock}
        buyerCountry={buyerCountry}
        onBlocked={onBlocked}
        initialDraft={initialDraft}
        onDraftChange={onDraftChange}
        onPurchase={onPurchase}
        metrics={metrics}
        surface="app_web"
      />
    </View>
  );
}

/** Out of the access and its sheet to the wallet, as the native outcome
 *  does (AttemptScreens.tsx). */
const openTickets = (accessId: string) =>
  router.replace({ pathname: "/(app)/(tabs)/profile/wallet", params: { showTicketAccessId: accessId } });

const styles = StyleSheet.create({
  screen: { gap: 12 },
  // The back's room is kept while it is hidden: nothing moves under it.
  header: { alignItems: "center", flexDirection: "row", minHeight: 28 },
  back: {
    alignItems: "center",
    height: 28,
    justifyContent: "center",
    marginLeft: -6,
    width: 28,
  },
  placeholder: { gap: 0 },
});
